import { Link } from 'react-router-dom';
import ShieldIcon from '../components/ShieldIcon';
import { brand } from '../config/brand';

export default function PrivacyPage() {
  return (
    <div className="legal-page">
      <div className="legal-topbar">
        <Link to="/" className="legal-topbar-brand">
          <ShieldIcon size={22} />
          {brand.name}
        </Link>
      </div>

      <main className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 2026</p>

        <p>
          This Privacy Policy describes how {brand.name} ("{brand.domain}") collects, uses, and
          protects information when you use our biometric liveness verification service. We are
          committed to safeguarding your privacy and handling your data with transparency.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We collect the following types of information depending on how you interact with our service:</p>
        <h3>Recruiter Accounts</h3>
        <ul>
          <li>Email address and authentication credentials (managed through AWS Cognito)</li>
          <li>Account activity such as session creation timestamps and verification results</li>
          <li>Phone number, if provided, for SMS notifications of completed checks</li>
        </ul>
        <h3>Candidates</h3>
        <ul>
          <li>Name, as provided by the recruiter when creating a verification session</li>
          <li>Facial imagery captured during the liveness check (see Biometric Data below)</li>
          <li>Verification result (pass/fail) and confidence score</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <p>We use collected information to:</p>
        <ul>
          <li>Provide and operate the liveness verification service</li>
          <li>Authenticate recruiter accounts and manage sessions</li>
          <li>Deliver verification results to the requesting recruiter</li>
          <li>Send SMS notifications when a candidate completes a check</li>
          <li>Maintain security and prevent fraud or misuse of the service</li>
        </ul>

        <h2>3. Biometric Data</h2>
        <p>
          {brand.name} uses AWS Rekognition Face Liveness to verify that a real person is present
          during a verification session. This is how biometric data is handled:
        </p>
        <ul>
          <li>
            Facial images are captured by your device's camera and streamed directly to AWS
            Rekognition via a secure WebSocket connection.
          </li>
          <li>
            AWS Rekognition processes this facial data in real-time to determine liveness. The
            processing is transient — images are analyzed and immediately discarded by AWS.
          </li>
          <li>
            <strong>{brand.name} does not store, retain, or have access to any biometric data,
            facial images, or facial feature vectors.</strong> No biometric templates or faceprints
            are created or persisted by our service.
          </li>
          <li>
            The only information retained from the liveness check is the pass/fail result and a
            numerical confidence score.
          </li>
        </ul>

        <h2>4. Data Retention</h2>
        <p>
          Verification session records — including candidate name, status, and confidence score — are
          stored in our database with a time-to-live (TTL) of 7 days. After this period, session
          records are automatically and permanently deleted. Recruiter account information is retained
          for the duration of the account's existence.
        </p>

        <h2>5. Third-Party Services</h2>
        <p>We rely on the following AWS services to operate {brand.name}:</p>
        <ul>
          <li>
            <strong>AWS Rekognition Face Liveness</strong> — performs the biometric liveness analysis.
            Facial data is processed transiently and subject to the{' '}
            <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">
              AWS Privacy Policy
            </a>.
          </li>
          <li>
            <strong>Amazon Cognito</strong> — manages recruiter authentication, including email/password
            and federated sign-in through Google and LinkedIn.
          </li>
          <li>
            <strong>Amazon DynamoDB</strong> — stores session metadata with automatic TTL-based expiration.
          </li>
          <li>
            <strong>Amazon SNS</strong> — delivers SMS notifications to recruiters upon check completion.
          </li>
        </ul>
        <p>
          We do not sell, rent, or share your personal information with third parties for marketing
          purposes.
        </p>

        <h2>6. Cookies and Local Storage</h2>
        <p>
          {brand.name} uses minimal browser storage, limited to authentication tokens required to
          maintain your signed-in session. We do not use tracking cookies, analytics cookies, or
          any third-party advertising cookies. The authentication tokens are managed by AWS Cognito
          and stored in your browser's local storage.
        </p>

        <h2>7. Security</h2>
        <p>
          All data transmitted between your browser and our service is encrypted using TLS (256-bit
          encryption). Facial data streams to AWS Rekognition use secure WebSocket connections.
          Recruiter credentials are managed by AWS Cognito with industry-standard hashing and
          encryption. Access to AWS resources is governed by least-privilege IAM policies.
        </p>

        <h2>8. Your Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Request information about what data we hold related to you</li>
          <li>Request deletion of your session data before the 7-day automatic expiration</li>
          <li>Request deletion of your recruiter account and all associated data</li>
          <li>Withdraw consent for a liveness check at any time by closing the browser window before completing the scan</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@aliv.me">privacy@aliv.me</a>.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          {brand.name} is not intended for use by individuals under the age of 18. We do not
          knowingly collect information from children. If you believe a child has used our service,
          please contact us so we can take appropriate action.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the "Last
          updated" date at the top of this page. Continued use of the service after changes are
          posted constitutes your acceptance of the revised policy.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy or our data practices, please contact
          us at <a href="mailto:privacy@aliv.me">privacy@aliv.me</a>.
        </p>

        <div className="legal-footer-links">
          <Link to="/terms">Terms of Service</Link>
          <Link to="/login">Sign In</Link>
        </div>
      </main>
    </div>
  );
}
